<template>
  <div>
    <label for="text" style="margin-right: 5px; white-space: nowrap">{{ data.label }}</label>
    <div>
      <span v-if="isActive || isDataTableField" class="setting-icon">
        <i class="el-icon-s-tools" @click="openSettings"></i>
      </span>
    </div>
    <div
      ref="table"
      :style="{
        display: 'grid',
        gridTemplateColumns: columnStyles,
        gridTemplateRows: rowStyles,
        gridGap: `${cellSpacing}px`,
        border: `${borderSize}px solid black`,
      }"
      class="resizable-table"
    >
      <div
        v-for="(cell, index) in tableCells"
        :key="index"
        :style="{
          border: `${borderSize}px solid black`,
          padding: `${cellPadding}px`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          position: 'relative',
          boxSizing: 'border-box',
        }"
      >
        <div
          v-if="(index + 1) % columns !== 0"
          class="resize-handle-col"
          @mousedown="startResize($event, index % columns)"
        ></div>
        <div
          v-if="index < (rows - 1) * columns || index >= (rows - 1) * columns"
          class="resize-handle-row"
          @mousedown="startRowResize($event, Math.floor(index / columns))"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'TableView',
    props: ['data', 'value', 'isActive', 'isDataTableField', 'isInDataTable'],
    data() {
      return {
        columnWidths: [],
        rowHeights: [],
        resizingColumnIndex: null,
        resizingRowIndex: null,
        startX: 0,
        startY: 0,
        startWidths: [],
        startHeights: [],
      };
    },
    computed: {
      rows() {
        return this.data.tableProperties.rows || 3;
      },
      columns() {
        return this.data.tableProperties.columns || 3;
      },
      borderSize() {
        return this.data.tableProperties.borderSize || 1;
      },
      cellSpacing() {
        return this.data.tableProperties.cellSpacing || 2;
      },
      cellPadding() {
        return this.data.tableProperties.cellPadding || 5;
      },
      tableCells() {
        return Array.from({ length: this.rows * this.columns }, (_, i) => `Cell ${i + 1}`);
      },
      columnStyles() {
        if (this.columnWidths.length === 0) {
          return `repeat(${this.columns}, 1fr)`;
        }
        return this.columnWidths.map((w) => `${w}px`).join(' ');
      },
      rowStyles() {
        if (this.rowHeights.length === 0) {
          return `repeat(${this.rows}, 50px)`;
        }
        return this.rowHeights.map((h) => `${h}px`).join(' ');
      },
    },
    mounted() {
      this.initializeColumnWidths();
      this.initializeRowHeights();
    },
    watch: {
      columns(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.initializeColumnWidths();
        }
      },
      rows(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.initializeRowHeights();
          this.$forceUpdate();
        }
      },
    },
    methods: {
      openSettings() {
        this.$emit('settings');
      },
      startResize(event, index) {
        if (this.columnWidths.length === 0) {
          const tableWidth = this.$refs.table ? this.$refs.table.offsetWidth : 600;
          const defaultWidth = tableWidth / this.columns;
          this.columnWidths = Array(this.columns).fill(defaultWidth);
        }

        this.resizingColumnIndex = index;
        this.startX = event.clientX;
        this.startWidths = [...this.columnWidths];
        document.addEventListener('mousemove', this.handleResize);
        document.addEventListener('mouseup', this.stopResize);
      },
      handleResize(event) {
        if (this.resizingColumnIndex === null) return;

        const deltaX = event.clientX - this.startX;
        const currentWidth = this.startWidths[this.resizingColumnIndex];
        let nextWidth = 0;

        if (this.resizingColumnIndex === this.columnWidths.length - 1) {
          nextWidth = 0;
        } else {
          nextWidth = this.startWidths[this.resizingColumnIndex + 1];
        }

        const newCurrentWidth = Math.max(currentWidth + deltaX, 50);
        const newNextWidth = Math.max(nextWidth - deltaX, 50);

        this.$set(this.columnWidths, this.resizingColumnIndex, newCurrentWidth);

        if (this.resizingColumnIndex < this.columnWidths.length - 1) {
          this.$set(this.columnWidths, this.resizingColumnIndex + 1, newNextWidth);
        }

        this.$emit('updateColumnWidths', [...this.columnWidths]);
      },
      startRowResize(event, index) {
        this.resizingRowIndex = index;
        this.startY = event.clientY;
        this.startHeights = [...this.rowHeights];
        document.addEventListener('mousemove', this.handleRowResize);
        document.addEventListener('mouseup', this.stopRowResize);
      },
      handleRowResize(event) {
        if (this.resizingRowIndex === null) return;

        const deltaY = event.clientY - this.startY;
        const currentHeight = this.startHeights[this.resizingRowIndex];
        if (this.resizingRowIndex < this.rowHeights.length - 1) {
          const nextHeight = this.startHeights[this.resizingRowIndex + 1];
          const newCurrentHeight = Math.max(currentHeight + deltaY, 30);
          const newNextHeight = Math.max(nextHeight - deltaY, 30);

          this.$set(this.rowHeights, this.resizingRowIndex, newCurrentHeight);
          this.$set(this.rowHeights, this.resizingRowIndex + 1, newNextHeight);
        } else {
          const newCurrentHeight = Math.max(currentHeight + deltaY, 30);
          this.$set(this.rowHeights, this.resizingRowIndex, newCurrentHeight);
        }

        this.$emit('updateRowHeights', [...this.rowHeights]);
      },
      initializeColumnWidths() {
        if (
          this.data.tableProperties.columnWidths &&
          this.data.tableProperties.columnWidths.length === this.columns
        ) {
          this.columnWidths = [...this.data.tableProperties.columnWidths];
        } else {
          this.columnWidths = [];
        }
        this.$emit('updateColumnWidths', [...this.columnWidths]);
      },
      initializeRowHeights() {
        if (
          this.data.tableProperties.rowHeights &&
          this.data.tableProperties.rowHeights.length === this.rows
        ) {
          this.rowHeights = [...this.data.tableProperties.rowHeights];
        } else {
          this.rowHeights = Array(this.rows).fill(50);
        }

        this.$emit('updateRowHeights', [...this.rowHeights]);
      },
      stopResize() {
        this.resizingColumnIndex = null;
        document.removeEventListener('mousemove', this.handleResize);
        document.removeEventListener('mouseup', this.stopResize);
      },
      stopRowResize() {
        this.resizingRowIndex = null;
        document.removeEventListener('mousemove', this.handleRowResize);
        document.removeEventListener('mouseup', this.stopRowResize);
      },
    },
  };
</script>

<style scoped>
  .setting-icon {
    cursor: pointer;
    margin-left: 5px;
    color: #409eff;
    font-size: 16px;
  }
  .setting-icon:hover {
    color: #66b1ff;
  }
  .resizable-table {
    position: relative;
    width: 100%;
    height: auto;
  }
  .resize-handle-col {
    position: absolute;
    top: 0;
    right: -5px;
    width: 10px;
    height: 100%;
    cursor: col-resize;
    z-index: 2;
    background: rgba(0, 0, 0, 0.1);
  }
  .resize-handle-col:hover {
    background: rgba(0, 0, 0, 0.2);
  }
  .resize-handle-row {
    position: absolute;
    bottom: -5px;
    left: 0;
    width: 100%;
    height: 10px;
    cursor: row-resize;
    z-index: 2;
    background: rgba(0, 0, 0, 0.1);
  }
  .resize-handle-row:hover {
    background: rgba(0, 0, 0, 0.2);
  }
</style>
